import React from 'react';
import { Modal, Steps } from 'antd';
import PrimaryButton from '../../../lib/primary-button';
import SecondaryButton from '../../../lib/secondary-button';
import checkPermission from '../../../../utils/permissions/permissions';
import { isLiveJob, isTerminalJob } from '../../../../utils/asset-type-import/i-import-job';
import { useImportAssetTypes } from './use-import-asset-types';
import { useIconBatchUpload } from './use-icon-batch-upload';
import { TWizardStep } from './i-import-wizard';
import UploadStep from './upload.step';
import MapColumnsStep from './map-columns.step';
import RunningStep from './running.step';
import IconsStep, { supportsDirectoryPicker } from './icons.step';
import ResultStep from './result.step';
import './import-asset-types.less';

const { Step } = Steps;

const STEP_ORDER: TWizardStep[] = ['upload', 'map', 'running', 'icons', 'result'];

interface IImportAssetTypesModalProps {
  onClose: () => void;
  onImported: () => void;
}

const ImportAssetTypesModal = ({
  onClose,
  onImported,
}: IImportAssetTypesModalProps): JSX.Element => {
  const wizard = useImportAssetTypes();
  const { state, job } = wizard;
  const icons = useIconBatchUpload();
  const readOnly = !checkPermission('assetTypes', 'write');

  const finish = (): void => {
    onImported();
    onClose();
  };

  /**
   * What closing means depends on where the bytes are. During the upload they
   * exist only in this tab, so closing really does abandon the import; once
   * the server has them, closing is harmless and the job carries on.
   *
   * The confirmation gate is the exception: the job is live only in the sense
   * that it is waiting for this window. Walking away without cancelling leaves
   * it waiting forever, and a tenant may have only one live job at a time, so
   * the next import would be refused. [AVP-901]
   */
  const requestClose = (): void => {
    if (job && job.state === 'validated') {
      Modal.confirm({
        title: 'Discard this import?',
        content: 'Nothing has been imported yet. The file will be discarded.',
        okText: 'Discard',
        cancelText: 'Keep it',
        onOk: () => {
          wizard.cancelJob();
          onClose();
        },
      });
      return;
    }

    if (job && isLiveJob(job.state)) {
      Modal.confirm({
        title: 'Leave this running?',
        content: 'The import keeps running on the server. You can reopen this window to check on it.',
        okText: 'Leave it running',
        cancelText: 'Stay here',
        onOk: finish,
      });
      return;
    }

    if (state.uploadId && !state.jobId) {
      Modal.confirm({
        title: 'Stop this upload?',
        content: 'Nothing has been imported yet.',
        okText: 'Stop',
        cancelText: 'Keep uploading',
        onOk: () => {
          wizard.cancelJob();
          onClose();
        },
      });
      return;
    }

    if (state.step === 'result' || job) {
      finish();
      return;
    }

    onClose();
  };

  const renderStep = (): JSX.Element | null => {
    switch (state.step) {
      case 'upload':
        return (
          <UploadStep
            fileName={state.fileName}
            fileError={state.fileError}
            parseErrors={state.prefix?.errors ?? []}
            busy={state.busy}
            estimatedRows={state.estimatedRows}
            fileBytes={state.fileBytes}
            sampleHeader={state.prefix?.header ?? []}
            sampleRows={state.prefix?.rows ?? []}
            sampleIsThin={wizard.advisories.length > 0 && !state.plan}
            resumable={state.resumable}
            onFileSelected={wizard.onFileSelected}
            onResume={wizard.onResume}
          />
        );
      case 'map':
        return state.plan && (
          <MapColumnsStep
            plan={state.plan}
            samples={wizard.samples}
            advisories={wizard.advisories}
            propertyNames={state.propertyNames}
            onChange={wizard.onPlanChange}
          />
        );
      case 'running':
        return (
          <RunningStep
            job={job}
            upload={state.upload}
            pollError={wizard.pollError}
            problems={state.problems}
            problemPage={state.problemPage}
            problemsLoading={state.problemsLoading}
            downloading={state.downloading}
            disabled={readOnly}
            onProblemPage={(page) => { wizard.loadProblems(page); }}
            onDownloadProblems={() => { wizard.downloadProblems(); }}
            onConfirm={() => wizard.goTo('icons')}
            onCancel={() => { wizard.cancelJob(); }}
            onRetryPoll={wizard.retryPoll}
          />
        );
      case 'icons':
        return (
          <IconsStep
            phase={icons.phase}
            files={icons.files}
            rejected={icons.rejected}
            progress={icons.progress}
            results={icons.results}
            supportsDirectory={supportsDirectoryPicker()}
            disabled={readOnly}
            error={icons.error}
            onFilesSelected={icons.onFilesSelected}
            onZipSelected={icons.onZipSelected}
            onUpload={() => { icons.upload(); }}
          />
        );
      case 'result':
        return (
          <ResultStep
            job={job}
            problems={state.problems}
            problemPage={state.problemPage}
            problemsLoading={state.problemsLoading}
            downloading={state.downloading}
            onProblemPage={(page) => { wizard.loadProblems(page); }}
            onDownloadProblems={() => { wizard.downloadProblems(); }}
          />
        );
      default:
        return null;
    }
  };

  const renderRunningFooter = (): JSX.Element => {
    if (job && job.state === 'validated') {
      const importable = Math.max(0, job.total - job.errorCount);
      return (
        <div className="import-wizard__footer">
          <SecondaryButton
            type="button"
            title="Cancel"
            disabled={state.busy}
            onClick={() => { wizard.cancelJob(); }}
          />
          <PrimaryButton
            type="button"
            title={importable === 1 ? 'Import 1 asset type' : `Import ${importable.toLocaleString()} asset types`}
            disabled={readOnly || importable === 0 || state.busy}
            loading={state.busy}
            onClick={() => { wizard.confirmImport(); }}
          />
        </div>
      );
    }

    if (job && isTerminalJob(job.state)) {
      return (
        <div className="import-wizard__footer">
          <PrimaryButton type="button" title="Continue" onClick={() => wizard.goTo('icons')} />
        </div>
      );
    }

    return (
      <div className="import-wizard__footer">
        <SecondaryButton type="button" title="Close" onClick={requestClose} />
      </div>
    );
  };

  const renderFooter = (): JSX.Element => {
    if (state.step === 'result') {
      return (
        <div className="import-wizard__footer">
          <PrimaryButton type="button" title="Done" onClick={finish} />
        </div>
      );
    }

    if (state.step === 'icons') {
      if (icons.phase === 'done') {
        return (
          <div className="import-wizard__footer">
            <PrimaryButton type="button" title="Continue" onClick={() => wizard.goTo('result')} />
          </div>
        );
      }

      const ready = icons.files.length;
      return (
        <div className="import-wizard__footer">
          <SecondaryButton type="button" title="Skip" onClick={() => wizard.goTo('result')} />
          <PrimaryButton
            type="button"
            title={ready === 1 ? 'Upload 1 icon' : `Upload ${ready} icons`}
            disabled={readOnly || ready === 0 || icons.phase !== 'ready'}
            loading={icons.phase === 'uploading'}
            onClick={() => { icons.upload(); }}
          />
        </div>
      );
    }

    if (state.step === 'running') {
      return renderRunningFooter();
    }

    return (
      <div className="import-wizard__footer">
        {state.step === 'map' && (
          <SecondaryButton type="button" title="Back" onClick={() => wizard.goTo('upload')} />
        )}
        <SecondaryButton type="button" title="Cancel" onClick={requestClose} />
        {state.step === 'map' && (
          <PrimaryButton
            type="button"
            title="Start import"
            disabled={readOnly || !wizard.canContinueFromMap}
            loading={state.busy}
            onClick={() => { wizard.startImport(); }}
          />
        )}
      </div>
    );
  };

  return (
    <Modal
      visible
      centered
      destroyOnClose
      maskClosable={false}
      width="90%"
      style={{ maxWidth: 1200 }}
      title="Import Asset Types"
      onCancel={requestClose}
      footer={renderFooter()}
    >
      <Steps size="small" current={STEP_ORDER.indexOf(state.step)}>
        <Step title="Upload" />
        <Step title="Map columns" />
        <Step title="Import" />
        <Step title="Icons" />
        <Step title="Done" />
      </Steps>
      <div className="import-wizard__body">{renderStep()}</div>
    </Modal>
  );
};

ImportAssetTypesModal.displayName = 'ImportAssetTypesModal';

export default ImportAssetTypesModal;
